import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { feedApi } from "../../../services/api/feedApi";
import { FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import "./PhotoPost.css";

const PhotoPost = ({ post, onUpdate }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [captionExpanded, setCaptionExpanded] = useState(false);

  const MAX_CAPTION_LENGTH = 100;

  // Auto-refresh media URLs after 9 minutes (they expire after 10)
  useEffect(() => {
    if (post.photos && post.photos.length > 0) {
      const timer = setTimeout(async () => {
        try {
          const response = await feedApi.getPost(post._id);
          if (onUpdate && response.data) {
            onUpdate(post._id, { photos: response.data.photos });
          }
        } catch (error) {
          // Silent fail for background refresh
        }
      }, 9 * 60 * 1000); // 9 minutes

      return () => clearTimeout(timer);
    }
  }, [post._id, post.photos, onUpdate]);

  // Open lightbox
  const openLightbox = (index) => {
    setSelectedPhotoIndex(index);
    setLightboxOpen(true);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  // Navigate to next photo
  const nextPhoto = (e) => {
    e.stopPropagation();
    setSelectedPhotoIndex((prev) =>
      prev === post.photos.length - 1 ? 0 : prev + 1
    );
  };

  // Navigate to previous photo
  const prevPhoto = (e) => {
    e.stopPropagation();
    setSelectedPhotoIndex((prev) =>
      prev === 0 ? post.photos.length - 1 : prev - 1
    );
  };

  const photoCount = post.photos?.length || 0;

  // Check if caption needs truncation
  const needsTruncation =
    post.content && post.content.length > MAX_CAPTION_LENGTH;
  const displayCaption =
    needsTruncation && !captionExpanded
      ? post.content.substring(0, MAX_CAPTION_LENGTH) + "..."
      : post.content;

  return (
    <div className="cbx-photo-post">
      {post.content && (
        <p className="cbx-photo-post__caption">
          {displayCaption}
          {needsTruncation && (
            <button
              className="cbx-photo-post__see-more"
              onClick={() => setCaptionExpanded(!captionExpanded)}
            >
              {captionExpanded ? "See less" : "See more"}
            </button>
          )}
        </p>
      )}

      {post.photos && post.photos.length > 0 && (
        <div
          className={`cbx-photo-post__grid cbx-photo-post__grid--${Math.min(
            photoCount,
            5
          )}`}
        >
          {post.photos.map((photo, index) => (
            <motion.div
              key={index}
              className="cbx-photo-post__item"
              onClick={() => openLightbox(index)}
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
            >
              <img
                src={photo.url || "/placeholder-image.png"}
                alt={photo.caption || `Photo ${index + 1}`}
                className="cbx-photo-post__image"
                loading="lazy"
              />
              {photo.caption && (
                <p className="cbx-photo-post__item-caption">{photo.caption}</p>
              )}
            </motion.div>
          ))}
        </div>
      )}

      {/* Tags */}
      {post.tags && post.tags.length > 0 && (
        <div className="cbx-photo-post__tags">
          {post.tags.map((tag, index) => (
            <motion.span
              key={index}
              className="cbx-photo-post__tag"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              #{tag}
            </motion.span>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            className="cbx-photo-post__lightbox"
            onClick={closeLightbox}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="cbx-photo-post__lightbox-content"
              onClick={(e) => e.stopPropagation()}
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              {/* Close Button */}
              <button
                className="cbx-photo-post__lightbox-close"
                onClick={closeLightbox}
              >
                <FiX size={24} />
              </button>

              {/* Previous Button */}
              {post.photos.length > 1 && (
                <button
                  className="cbx-photo-post__lightbox-prev"
                  onClick={prevPhoto}
                >
                  <FiChevronLeft size={24} />
                </button>
              )}

              {/* Image */}
              <img
                src={post.photos[selectedPhotoIndex]?.url}
                alt={
                  post.photos[selectedPhotoIndex]?.caption || "Full size photo"
                }
                className="cbx-photo-post__lightbox-image"
              />

              {/* Caption */}
              {post.photos[selectedPhotoIndex]?.caption && (
                <div className="cbx-photo-post__lightbox-caption">
                  {post.photos[selectedPhotoIndex].caption}
                </div>
              )}

              {/* Next Button */}
              {post.photos.length > 1 && (
                <button
                  className="cbx-photo-post__lightbox-next"
                  onClick={nextPhoto}
                >
                  <FiChevronRight size={24} />
                </button>
              )}

              {/* Photo Counter */}
              {post.photos.length > 1 && (
                <div className="cbx-photo-post__lightbox-counter">
                  {selectedPhotoIndex + 1} / {post.photos.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhotoPost;
